import React, { useEffect, useRef, useState } from 'react';
import PageTitle from '../../FormElements/PageTitle';
import LandingPageExitMenu from '../../Navigation/LandingPageExitMenu';
import getLandingImageHeight from '../../../utils/getLandingImageHeight';
import PlayerSearchBarResultsList from '../../SearchBars/PlayerSearchBar/PlayerSearchBarResultsList';
import VideoCarousel from '../../VideoCarousel/VideoCarousel';
import ClassroomImage from '../../../../assets/images/classroom.jpg';
import { useAuth } from '../../../contexts/AuthContext';
import useSubscriptionGate from '../../../hooks/useSubscriptionGate';
import htmlToPlainTextWithLists from '../../../utils/htmlToPlainTextWithLists';

const MAX_WHITEBOARDS = 3;

export default function ClassroomPage({
	get_players_endpoint,
	get_player_endpoint
}) {
	const { user, csrfToken, endpoints } = useAuth();
	const { guardSubscription } = useSubscriptionGate();
	const [isFilmRoomOpen, setIsFilmRoomOpen] = useState(false);
	const [isWhiteboardOpen, setIsWhiteboardOpen] = useState(false);
	const [search, setSearch] = useState('');
	const [debouncedSearch, setDebouncedSearch] = useState('');
	const [players, setPlayers] = useState([]);
	const [selectedPlayer, setSelectedPlayer] = useState(null);
	const [selectedPlayerVideos, setSelectedPlayerVideos] = useState([]);
	const [isLoadingPlayer, setIsLoadingPlayer] = useState(false);
	const [isSearchingPlayers, setIsSearchingPlayers] = useState(false);
	const [hasSearchedPlayers, setHasSearchedPlayers] = useState(false);
	const [boards, setBoards] = useState(['']);
	const [boardIds, setBoardIds] = useState([]);
	const [isSavingWhiteboards, setIsSavingWhiteboards] = useState(false);
	const [isSavingWhiteboardsAi, setIsSavingWhiteboardsAi] = useState(false);
	const [whiteboardModalTab, setWhiteboardModalTab] = useState('write');
	const [savedWhiteboards, setSavedWhiteboards] = useState([]);
	const [savedListLoading, setSavedListLoading] = useState(false);
	const [savedListError, setSavedListError] = useState(null);
	const filmSearchInputRef = useRef(null);

	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedSearch(search.trim());
		}, 200);

		return () => clearTimeout(timer);
	}, [search]);

	useEffect(() => {
		if (!isFilmRoomOpen || !debouncedSearch) {
			setPlayers([]);
			setIsSearchingPlayers(false);
			if (!search.trim()) {
				setHasSearchedPlayers(false);
			}
			return;
		}

		const abortController = new AbortController();
		const fetchPlayers = async () => {
			try {
				setIsSearchingPlayers(true);
				const response = await fetch(
					`${get_players_endpoint}?search=${encodeURIComponent(debouncedSearch)}`,
					{ signal: abortController.signal }
				);
				const result = await response.json();
				setPlayers(result.players || []);
				setHasSearchedPlayers(true);
			} catch (error) {
				if (error.name !== 'AbortError') {
					console.error('Error fetching players for film room:', error);
				}
			} finally {
				setIsSearchingPlayers(false);
			}
		};

		fetchPlayers();
		return () => abortController.abort();
	}, [
		debouncedSearch,
		search,
		isFilmRoomOpen,
		get_players_endpoint
	]);

	useEffect(() => {
		if (isFilmRoomOpen && !selectedPlayer) {
			const timer = setTimeout(() => {
				if (filmSearchInputRef.current) {
					filmSearchInputRef.current.focus();
				}
			}, 0);
			return () => clearTimeout(timer);
		}
	}, [
		isFilmRoomOpen,
		selectedPlayer
	]);

	const handleFilmPlayerClick = async (player) => {
		setSelectedPlayer(player);
		setIsLoadingPlayer(true);
		setSearch('');
		setPlayers([]);

		try {
			const response = await fetch(
				`${get_player_endpoint.replace('id', player.id)}?position=${encodeURIComponent(player.position)}`
			);
			const result = await response.json();
			const playerData = result.data ? result.data : {};
			const videos = [
				playerData.video_url_1,
				playerData.video_url_2,
				playerData.video_url_3
			].filter(Boolean);
			setSelectedPlayerVideos(videos);
		} catch (error) {
			console.error('Error loading player videos:', error);
			setSelectedPlayerVideos([]);
		} finally {
			setIsLoadingPlayer(false);
		}
	};

	const closeFilmRoom = () => {
		setIsFilmRoomOpen(false);
		setSearch('');
		setDebouncedSearch('');
		setPlayers([]);
		setSelectedPlayer(null);
		setSelectedPlayerVideos([]);
		setIsLoadingPlayer(false);
		setIsSearchingPlayers(false);
		setHasSearchedPlayers(false);
	};

	const resetFilmSelection = () => {
		setSearch('');
		setDebouncedSearch('');
		setPlayers([]);
		setSelectedPlayer(null);
		setSelectedPlayerVideos([]);
		setIsLoadingPlayer(false);
		setIsSearchingPlayers(false);
		setHasSearchedPlayers(false);
	};

	const addWhiteboard = () => {
		if (boards.length >= MAX_WHITEBOARDS) return;
		setBoards((prevBoards) => [...prevBoards, '']);
		setBoardIds((prev) => [...prev, null]);
	};

	const removeWhiteboard = () => {
		if (boards.length <= 1) return;
		setBoards((prevBoards) => prevBoards.slice(0, -1));
		setBoardIds((prev) => prev.slice(0, -1));
	};

	const updateBoard = (index, value) => {
		setBoards((prevBoards) =>
			prevBoards.map((board, boardIndex) =>
				boardIndex === index ? value : board
			)
		);
	};

	const whiteboardPayload = (withAi) => ({
		whiteboards: boards.map((content, index) => ({
			id: boardIds[index] != null ? boardIds[index] : null,
			content,
			position: index
		})),
		with_ai: withAi
	});

	const mergeRowsIntoPanels = (rows) => {
		if (!Array.isArray(rows)) return;
		const positionOf = (row) => {
			const v = row.position != null ? row.position : row['position'];
			const n = typeof v === 'number' ? v : parseInt(String(v), 10);
			return Number.isNaN(n) ? null : n;
		};
		const idOf = (row) => {
			const v = row.id != null ? row.id : row['id'];
			if (v === null || v === undefined || v === '') return null;
			const n = typeof v === 'number' ? v : parseInt(String(v), 10);
			return Number.isNaN(n) ? null : n;
		};
		const contentOf = (row) => {
			const v = row.content != null ? row.content : row['content'];
			if (v == null) return '';
			return htmlToPlainTextWithLists(String(v));
		};
		setBoards((prevBoards) => {
			const next = [...prevBoards];
			rows.forEach((row) => {
				const p = positionOf(row);
				if (p == null || p < 0) return;
				while (next.length <= p) next.push('');
				next[p] = contentOf(row);
			});
			return next;
		});
		setBoardIds((prevIds) => {
			const next = [...prevIds];
			rows.forEach((row) => {
				const p = positionOf(row);
				if (p == null || p < 0) return;
				while (next.length <= p) next.push(null);
				next[p] = idOf(row);
			});
			return next;
		});
	};

	const replaceUserId = (template) => template.replace('user_id', String(user.id));

	const whiteboardDeleteUrl = (recordId) => {
		const base = replaceUserId(endpoints.delete_user_whiteboard_endpoint);
		return base.replace('/whiteboards/id', `/whiteboards/${recordId}`);
	};

	const previewWhiteboardContent = (html) => {
		const plain = htmlToPlainTextWithLists(String(html ?? '')).trim();
		if (plain.length <= 120) return plain || '—';
		return `${plain.slice(0, 120)}…`;
	};

	const formatWhiteboardUpdatedAt = (iso) => {
		if (!iso) return '—';
		try {
			return new Date(iso).toLocaleString(undefined, {
				month: 'long',
				day: 'numeric',
				year: 'numeric',
				hour: 'numeric',
				minute: '2-digit'
			});
		} catch {
			return '—';
		}
	};

	const fetchSavedWhiteboards = async () => {
		if (!user?.id || !endpoints?.get_user_whiteboards_endpoint) return;
		setSavedListLoading(true);
		setSavedListError(null);
		try {
			const url = `${replaceUserId(endpoints.get_user_whiteboards_endpoint)}?limit=100`;
			const response = await fetch(url, {
				method: 'GET',
				headers: { Accept: 'application/json' }
			});
			const data = await response.json();
			if (!response.ok || !data.success) {
				throw new Error(data.error || 'Could not load saved whiteboards.');
			}
			setSavedWhiteboards(Array.isArray(data.data) ? data.data : []);
		} catch (err) {
			console.error(err);
			setSavedListError(err.message || 'Could not load saved whiteboards.');
			setSavedWhiteboards([]);
		} finally {
			setSavedListLoading(false);
		}
	};

	useEffect(() => {
		if (!isWhiteboardOpen || whiteboardModalTab !== 'saved' || !user?.id) return;
		fetchSavedWhiteboards();
	}, [isWhiteboardOpen, whiteboardModalTab, user?.id]);

	const openSavedWhiteboardForEdit = (wb) => {
		const plain = htmlToPlainTextWithLists(String(wb.content ?? ''));
		setBoards([plain]);
		setBoardIds([wb.id]);
		setWhiteboardModalTab('write');
	};

	const deleteSavedWhiteboard = async (wb) => {
		if (!user?.id || !endpoints?.delete_user_whiteboard_endpoint) return;
		if (!window.confirm('Delete this saved whiteboard?')) return;
		try {
			const response = await fetch(whiteboardDeleteUrl(wb.id), {
				method: 'DELETE',
				headers: { 'X-CSRF-Token': csrfToken }
			});
			const data = await response.json().catch(() => ({}));
			if (!response.ok || !data.success) {
				throw new Error(data.error || 'Delete failed.');
			}
			setSavedWhiteboards((prev) => prev.filter((row) => row.id !== wb.id));
		} catch (err) {
			console.error(err);
		}
	};

	const closeWhiteboardModal = () => {
		setIsWhiteboardOpen(false);
		setWhiteboardModalTab('write');
		setBoards(['']);
		setBoardIds([]);
		setSavedWhiteboards([]);
		setSavedListError(null);
	};

	const postClassroomWhiteboards = async ({ withAi }) => {
		if (!user?.id || !endpoints?.upsert_user_whiteboards_endpoint) return;
		if (withAi) {
			setIsSavingWhiteboardsAi(true);
		} else {
			setIsSavingWhiteboards(true);
		}
		try {
			const url = replaceUserId(endpoints.upsert_user_whiteboards_endpoint);
			const response = await fetch(url, {
				method: 'PATCH',
				headers: {
					'Content-Type': 'application/json',
					'X-CSRF-Token': csrfToken
				},
				body: JSON.stringify(whiteboardPayload(withAi))
			});
			const data = await response.json();
			if (!response.ok || !data.success) {
				throw new Error(data.error || 'Save failed.');
			}
			mergeRowsIntoPanels(data.data);
		} catch (err) {
			console.error(err);
		} finally {
			if (withAi) {
				setIsSavingWhiteboardsAi(false);
			} else {
				setIsSavingWhiteboards(false);
			}
		}
	};

	const handleWhiteboardSave = () => {
		postClassroomWhiteboards({ withAi: false });
	};

	const handleWhiteboardSaveWithAi = () => {
		postClassroomWhiteboards({ withAi: true });
	};

	const isFilmVideoView = Boolean(selectedPlayer);

	return (
		<div className="container p-4 pt-4 lg:py-16 md:px-20">
			<div>
				<PageTitle title="The Classroom" />
			</div>

			<div className="relative mt-4 rounded-lg overflow-hidden shadow-lg">
				<img
					src={ClassroomImage}
					alt="The Classroom"
					decoding="async"
					className="block w-full object-cover object-center"
					style={getLandingImageHeight()}
				/>

				<div className="absolute bottom-2 md:bottom-4 left-1/2 z-10 -translate-x-1/2 transform">
					<div className="flex items-center justify-center gap-6 p-3 rounded-full">
						<div
							className="w-12 h-12 flex items-center justify-center bg-blue-500 border border-gray-200 text-white text-2xl rounded-full cursor-pointer hover:scale-105 transition-transform transform backdrop-blur-md"
							onClick={() => guardSubscription(() => (window.location.href = '/courses'))}
							title="Courses"
						>
							📚
						</div>
						<div
							className="w-12 h-12 flex items-center justify-center bg-blue-500 border border-gray-200 text-white text-2xl rounded-full cursor-pointer hover:scale-105 transition-transform transform backdrop-blur-md"
							onClick={() => setIsFilmRoomOpen(true)}
							title="Film Room"
						>
							🎥
						</div>
						<div
							className="w-12 h-12 flex items-center justify-center bg-blue-500 border border-gray-200 text-white text-2xl rounded-full cursor-pointer hover:scale-105 transition-transform transform backdrop-blur-md"
							onClick={() => guardSubscription(() => setIsWhiteboardOpen(true))}
							title="Whiteboard"
						>
							⬜
						</div>
						<LandingPageExitMenu
							currentPage="classroom"
							triggerTitle="Leave The Classroom"
							containerClassName="relative flex items-center md:hidden"
							triggerClassName="w-12 h-12 flex items-center justify-center bg-blue-500 border border-gray-200 text-white text-2xl rounded-full cursor-pointer hover:scale-105 transition-transform transform backdrop-blur-md"
						/>
					</div>
				</div>
				<div className="hidden md:block absolute bottom-4 right-4 z-10">
					<LandingPageExitMenu
						currentPage="classroom"
						triggerTitle="Leave The Classroom"
					/>
				</div>
			</div>
			{isFilmRoomOpen && (
				<div
					className="fixed inset-0 flex items-center justify-center bg-black p-0"
					style={{ zIndex: 200 }}
					onClick={closeFilmRoom}
				>
					<div
						className={`relative rounded-lg overflow-visible bg-white dark:bg-dark-card border border-gray-200 dark:border-dark-border ${
							isFilmVideoView ? 'w-full max-w-6xl p-0' : 'w-auto p-6'
						}`}
						style={
							isFilmVideoView
								? (window.innerWidth < 640
									? { width: '100%' }
									: getLandingImageHeight())
								: { maxWidth: '90vw', maxHeight: '90vh' }
						}
						onClick={(event) => event.stopPropagation()}
					>
						{!selectedPlayer ? (
							<div className="flex items-center justify-center">
								<div className="relative w-auto">
									<input
										ref={filmSearchInputRef}
										type="text"
										value={search}
										placeholder="Search for a player"
										onChange={(event) => setSearch(event.target.value)}
										onKeyDown={(event) => {
											if (event.key === 'Enter') {
												event.preventDefault();
											}
										}}
										className="w-72 md:w-96 p-3 border border-gray-300 dark:border-dark-border rounded-md text-base bg-white dark:bg-dark-bg text-gray-900 dark:text-dark-text"
									/>
									{isSearchingPlayers && (
										<div className="absolute z-30 w-full mt-2 rounded-md border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-card px-4 py-3 text-sm text-gray-500 dark:text-gray-300">
											Searching...
										</div>
									)}
									{players.length > 0 && (
										<PlayerSearchBarResultsList
											players={players}
											handlePlayerClick={handleFilmPlayerClick}
											containerClassName="absolute bg-white dark:bg-dark-card border border-gray-200 dark:border-dark-border mt-2 p-2 rounded-md shadow-lg z-30 max-h-64 overflow-y-auto w-full"
											containerStyle={{ top: '100%', left: 0 }}
										/>
									)}
									{!isSearchingPlayers &&
										hasSearchedPlayers &&
										debouncedSearch &&
										players.length === 0 && (
											<div className="absolute z-30 w-full mt-2 rounded-md border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-card px-4 py-3 text-sm text-gray-500 dark:text-gray-300">
												No matching players
											</div>
										)}
								</div>
							</div>
						) : (
							<div>
								{isLoadingPlayer ? (
									<div className="py-10 text-gray-600 dark:text-gray-300">
										Loading videos...
									</div>
								) : selectedPlayerVideos.length > 0 ? (
									<div className="overflow-hidden rounded-lg">
										<VideoCarousel videos={selectedPlayerVideos} />
									</div>
								) : (
									<div className="h-full w-full p-6 md:p-10 flex items-center justify-center">
										<div className="w-full max-w-2xl rounded-2xl bg-white/90 dark:bg-dark-card/90 px-8 py-12 text-center">
											<p className="text-lg md:text-xl font-semibold tracking-tight text-gray-700 dark:text-gray-200">
												No videos available for this player yet.
											</p>
										</div>
									</div>
								)}
							</div>
						)}
					</div>
				</div>
			)}
			{isWhiteboardOpen && (
				<div
					className="fixed inset-0 flex items-center justify-center bg-black p-4"
					style={{ zIndex: 200 }}
				>
					<button
						type="button"
						onClick={closeWhiteboardModal}
						className="absolute right-4 top-4 z-10 text-4xl leading-none text-white hover:opacity-80"
						title="Close Whiteboard"
					>
						×
					</button>
					<div
						className="relative flex w-full max-w-6xl flex-col overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-dark-border dark:bg-dark-card"
						style={{
							width: '100%',
							height: '80vh',
							maxHeight: '80vh'
						}}
					>
						<div className="flex shrink-0 gap-1 border-b border-gray-200 px-3 pt-3 dark:border-dark-border md:px-4 md:pt-4">
							<button
								type="button"
								onClick={() => setWhiteboardModalTab('write')}
								className={`rounded-t-md px-3 py-2 text-sm font-semibold md:px-4 md:text-base ${
									whiteboardModalTab === 'write'
										? 'border-b-2 border-gray-900 text-gray-900 dark:border-dark-text dark:text-dark-text'
										: 'text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
								}`}
							>
								New
							</button>
							<button
								type="button"
								onClick={() => setWhiteboardModalTab('saved')}
								className={`rounded-t-md px-3 py-2 text-sm font-semibold md:px-4 md:text-base ${
									whiteboardModalTab === 'saved'
										? 'border-b-2 border-gray-900 text-gray-900 dark:border-dark-text dark:text-dark-text'
										: 'text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
								}`}
							>
								Saved
							</button>
						</div>
						{whiteboardModalTab === 'write' ? (
							<div className="relative flex min-h-0 flex-1 flex-col px-3 pb-3 pt-2 md:px-4 md:pb-4">
								<div className="relative h-full min-h-0 flex-1">
									<div
										className={`grid h-full min-h-0 gap-3 overflow-y-auto ${
											boards.length === 1
												? 'grid-cols-1'
												: boards.length === 2
													? 'grid-cols-1 md:grid-cols-2'
													: 'grid-cols-1 md:grid-cols-3'
										}`}
									>
										{boards.map((board, index) => (
											<textarea
												key={`classroom-whiteboard-${index}`}
												value={board}
												onChange={(event) => updateBoard(index, event.target.value)}
												placeholder=""
												className="h-full w-full resize-none rounded-md border border-gray-300 bg-white p-3 text-2xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-dark-border dark:bg-dark-bg dark:text-dark-text"
												style={{ minHeight: '140px' }}
											/>
										))}
									</div>
									<div className="pointer-events-none absolute inset-0 z-30 flex items-end justify-center pb-2">
										<div className="pointer-events-auto flex items-center gap-2 rounded-full bg-black px-3 py-1.5 text-white shadow-lg bg-opacity-80">
											<button
												type="button"
												onClick={handleWhiteboardSaveWithAi}
												disabled={
													isSavingWhiteboardsAi ||
													isSavingWhiteboards ||
													!user?.id
												}
												className="rounded-md bg-violet-600 px-2 py-1 text-xs font-medium hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-50 md:px-3 md:py-1.5 md:text-sm"
											>
												{isSavingWhiteboardsAi ? 'Saving with AI…' : 'Save with AI'}
											</button>
											<button
												type="button"
												onClick={handleWhiteboardSave}
												disabled={
													isSavingWhiteboards ||
													isSavingWhiteboardsAi ||
													!user?.id
												}
												className="rounded-md bg-blue-600 px-2 py-1 text-xs font-medium hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50 md:px-3 md:py-1.5 md:text-sm"
											>
												{isSavingWhiteboards ? 'Saving…' : 'Save'}
											</button>
										</div>
									</div>
								</div>
							</div>
						) : (
							<div className="flex min-h-0 flex-1 flex-col overflow-hidden px-3 pb-3 pt-2 md:px-4 md:pb-4">
								{savedListLoading ? (
									<p className="text-sm text-gray-600 dark:text-gray-400">Loading…</p>
								) : savedListError ? (
									<p className="text-sm text-red-600 dark:text-red-400">{savedListError}</p>
								) : (
									<div className="flex min-h-0 flex-1 flex-col overflow-hidden">
										<div className="flex shrink-0 border-b-2 border-gray-900 pb-2 text-sm font-bold text-gray-900 dark:border-dark-text dark:text-dark-text">
											<div className="min-w-0 flex-1 pr-2">Content</div>
											<div className="w-40 shrink-0 md:w-48">Updated at</div>
											<div className="w-28 shrink-0 text-right md:w-32">Actions</div>
										</div>
										<div className="min-h-0 flex-1 overflow-y-auto">
											{savedWhiteboards.length === 0 ? (
												<p className="py-6 text-center text-sm text-gray-500 dark:text-gray-400">
													No saved whiteboards yet.
												</p>
											) : (
												savedWhiteboards.map((wb, rowIndex) => (
													<div
														key={wb.id}
														className={`flex border-b border-gray-200 py-2 text-sm dark:border-dark-border ${
															rowIndex % 2 === 1
																? 'bg-gray-50 dark:bg-gray-900'
																: ''
														}`}
													>
														<div
															className="min-w-0 flex-1 truncate pr-2 text-gray-800 dark:text-dark-text"
															title={htmlToPlainTextWithLists(String(wb.content ?? ''))}
														>
															{previewWhiteboardContent(wb.content)}
														</div>
														<div className="w-40 shrink-0 text-gray-600 dark:text-gray-400 md:w-48">
															{formatWhiteboardUpdatedAt(wb.updated_at)}
														</div>
														<div className="flex w-28 shrink-0 items-start justify-end gap-2 md:w-32">
															<button
																type="button"
																onClick={() => openSavedWhiteboardForEdit(wb)}
																className="text-blue-600 hover:underline dark:text-blue-400"
																title="Edit"
															>
																Edit
															</button>
															<button
																type="button"
																onClick={() => deleteSavedWhiteboard(wb)}
																className="text-blue-600 hover:underline dark:text-blue-400"
																title="Delete"
															>
																Delete
															</button>
														</div>
													</div>
												))
											)}
										</div>
									</div>
								)}
							</div>
						)}
					</div>
					{whiteboardModalTab === 'write' ? (
						<div className="absolute bottom-4 left-1/2 z-10 flex -translate-x-1/2 transform items-center gap-2 rounded-full bg-black bg-opacity-70 px-3 py-1.5 text-white">
							{boards.length > 1 ? (
								<button
									type="button"
									onClick={removeWhiteboard}
									className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 bg-blue-500 text-xl leading-none text-white transition-transform hover:scale-105"
									title="Remove last whiteboard"
								>
									−
								</button>
							) : null}
							<div className="text-xs font-medium md:text-sm">
								{boards.length}/{MAX_WHITEBOARDS}
							</div>
							{boards.length < MAX_WHITEBOARDS ? (
								<button
									type="button"
									onClick={addWhiteboard}
									className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 bg-blue-500 text-xl leading-none text-white transition-transform hover:scale-105"
									title="Add whiteboard"
								>
									+
								</button>
							) : null}
						</div>
					) : null}
				</div>
			)}
		</div>
	);
}
